import type { CustomValueWarningField } from '@aurora/shared-types/customValueWarning';
import { getLog } from '@aurora/shared-utils/log';
import React, { useRef } from 'react';
import { Form, useClassNameMapper } from 'react-bootstrap';
import type { FieldValues } from 'react-hook-form';
import type { FieldPath } from 'react-hook-form/dist/types/path';
import { isCategorizedPossibleValuesField } from '../../../helpers/form/FormHelper/FormHelper';
import ComponentHelper from '../../../helpers/ui/ComponentHelper/ComponentHelper';
import CustomValueWarningWrapper from '../CustomValueWarning/CustomValueWarningWrapper';
import type { FormFieldVariant } from '../enums';
import FormField from '../FormField/FormField';
import type { FormFieldProps, PossibleValuesField } from '../types';
import { FieldType } from '../types';
import localStyles from './SelectField.module.pcss';

const log = getLog(module);

/**
 * Select field specification to render select field in the form. NameT is the name of the field and DataT is the
 * definition of bean that holds all the fields for the form.
 */
export interface SelectFieldSpec<NameT extends FieldPath<DataT>, DataT extends FieldValues>
  extends CustomValueWarningField<string>,
    PossibleValuesField<NameT, DataT, FormFieldVariant.SELECT> {
  /**
   * If present, will be applied to the select input for use with screen readers.
   */
  ariaLabel?: string;
}

/**
 * Renders select field for the form
 *
 * @author Manish Shrestha
 */
const SelectField = <NameT extends FieldPath<DataT>, DataT extends FieldValues>({
  fieldSpec,
  formId,
  i18n,
  className,
  formMethods,
  onFormSubmit,
  submitOnChange,
  allFields
}: FormFieldProps<NameT, DataT, SelectFieldSpec<NameT, DataT>>): React.ReactElement => {
  const {
    formState: { errors },
    getValues
  } = formMethods;
  const cx = useClassNameMapper(localStyles);
  const { formatMessage, loading: textLoading } = i18n;
  const {
    ariaLabel,
    customValueWarningOptions,
    name,
    attributes,
    focus,
    focusHandler,
    disabled,
    values
  } = fieldSpec;

  const hasBeenFocused = useRef(false);

  if (isCategorizedPossibleValuesField(values)) {
    log.error('Categorized possible value field is not yet supported');
    return null;
  }

  if (textLoading) {
    return null;
  }

  const valuesSet = new Set(values.map(value => value.value));

  return (
    <FormField
      fieldSpec={fieldSpec}
      fieldType={FieldType.UNCONTROLLED}
      formId={formId}
      i18n={i18n}
      formMethods={formMethods}
      onFormSubmit={onFormSubmit}
      submitOnChange={submitOnChange}
      allFormFields={allFields}
      className={cx(className)}
    >
      {({
        render: {
          register: { ref, onBlur },
          onChange
        }
      }) => {
        return (
          <>
            <CustomValueWarningWrapper
              options={customValueWarningOptions}
              defaultLogic={currentValue => !valuesSet.has(currentValue)}
              value={getValues(name)}
            />
            <Form.Control
              aria-label={ariaLabel}
              name={name}
              as="select"
              ref={(element): void => {
                ref(element);
                if (focus) {
                  ComponentHelper.focusHandler(hasBeenFocused, element, focusHandler);
                }
              }}
              isInvalid={errors[name] !== undefined}
              disabled={disabled}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...attributes}
              data-testid={`SelectField.${formId}.${name}`}
              onChange={onChange}
              onBlur={onBlur}
              className={cx('lia-select-field')}
            >
              {values.map(option => {
                const { label, key, isDisabled } = option;
                return (
                  <option
                    key={`${formId}-${fieldSpec.name}-${key}`}
                    value={key}
                    disabled={isDisabled}
                    data-testid={`SelectField.${formId}.${name}.option.${key}`}
                  >
                    {label || formatMessage(`${formId}.${name}.${key}.label`)}
                  </option>
                );
              })}
            </Form.Control>
          </>
        );
      }}
    </FormField>
  );
};

export default SelectField;
